import { AppShell, PageTitle } from "@/components/app-shell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TableSkeleton } from "@/components/mission-skeletons";
import { cancelJob, enqueueJob, type Job, listJobs, shortOid } from "@/lib/api";
import { isNonDeterministicJobKind, isSqlMindCheckKind, jobTypeLabel, MIND_CHECK_JOB_NOTE } from "@/lib/job-kind-labels";
import {
  JOBS_FOOTER,
  STALE_QUEUED_BODY,
  STALE_QUEUED_MS,
  STALE_QUEUED_TECH,
  STALE_QUEUED_TITLE,
  formatJobStartedAt,
  staleQueuedJobs,
  userFacingJobError,
} from "@/lib/job-drain";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";

const tone: Record<string, string> = {
  succeeded: "border-border bg-transparent text-foreground",
  passed: "border-border bg-transparent text-foreground",
  queued: "border-border bg-transparent text-muted-foreground",
  running: "border-falcon bg-falcon-muted text-falcon",
  pending: "border-border bg-transparent text-muted-foreground",
  failed: "border-falcon bg-falcon-muted text-falcon",
  cancelled: "border-border bg-transparent text-muted-foreground",
  timed_out: "border-falcon bg-falcon-muted text-falcon",
  inconclusive: "border-border bg-transparent text-muted-foreground",
};

function jobStatusLabel(job: Job): string {
  return job.check_status || job.state;
}

export function JobsPage() {
  const { orgSlug, projectSlug, jobId } = useParams();
  const [params] = useSearchParams();
  const stateFilter = params.get("filter") || "all";
  const [items, setItems] = useState<Job[]>([]);
  const [current, setCurrent] = useState<Job | null>(null);
  const [open, setOpen] = useState(Boolean(jobId));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { loading, start, finish } = useKeyedLoad(routeKey);

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug) {
      finish(isCurrent);
      return;
    }
    try {
      const body = await listJobs(orgSlug, projectSlug);
      if (!isCurrent()) return;
      setItems(body.items ?? []);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setItems([]);
      setErr(e instanceof Error ? e.message : "jobs load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setCurrent(null);
    setOpen(Boolean(jobId));
  }, [orgSlug, projectSlug]);

  useEffect(() => {
    if (!jobId) return;
    const found = items.find((j) => j.id === jobId);
    if (found) {
      setCurrent(found);
      setOpen(true);
    }
  }, [jobId, items]);

  const visible = useMemo(
    () => items.filter((j) => stateFilter === "all" || jobStatusLabel(j) === stateFilter || j.state === stateFilter),
    [items, stateFilter],
  );
  const staleQueued = useMemo(() => staleQueuedJobs(items, Date.now(), STALE_QUEUED_MS), [items]);
  const retryTarget = staleQueued.find((j) => !isNonDeterministicJobKind(j.kind, j.kind_detail));
  const staleMindCheck = staleQueued.some((j) => isSqlMindCheckKind(j.kind));
  const staleStub = staleQueued.some(
    (j) => isNonDeterministicJobKind(j.kind, j.kind_detail) && !isSqlMindCheckKind(j.kind),
  );

  async function retry(job: Job) {
    if (!orgSlug || !projectSlug) return;
    if (isNonDeterministicJobKind(job.kind, job.kind_detail)) {
      setErr(isSqlMindCheckKind(job.kind) ? MIND_CHECK_JOB_NOTE : "stub job kind — not a deterministic JobsChecks run");
      return;
    }
    setBusy(true);
    try {
      await enqueueJob(orgSlug, projectSlug, {
        kind: job.kind,
        kind_detail: job.kind_detail,
        review_id: job.review_id,
        artifact_id: job.artifact_id,
      });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "retry failed");
    } finally {
      setBusy(false);
    }
  }

  async function cancel(job: Job) {
    if (!orgSlug || !projectSlug) return;
    setBusy(true);
    try {
      await cancelJob(orgSlug, projectSlug, job.id);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "cancel failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title={<PageTitle kicker="04 Verify · off-isolate rsci">Jobs</PageTitle>}>
      {err && <p className="mb-3 text-sm text-muted-foreground">{err}</p>}
      {loading ? (
        <TableSkeleton columns={5} testId="jobs-skeleton" />
      ) : (
      <>
      {staleQueued.length > 0 && (
        <Alert className="mb-4" data-testid="stale-queued-banner">
          <AlertTitle>{STALE_QUEUED_TITLE}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              {staleQueued.length} job{staleQueued.length === 1 ? "" : "s"} queued longer than 5 min. {STALE_QUEUED_BODY}
            </p>
            <p className="text-xs text-muted-foreground">{STALE_QUEUED_TECH}</p>
            {staleMindCheck && (
              <p className="text-xs text-muted-foreground" data-testid="mind-check-not-a-gate">
                {MIND_CHECK_JOB_NOTE}
              </p>
            )}
            {staleStub && (
              <p className="text-xs text-muted-foreground">
                stub job kind — not a deterministic JobsChecks run. Cancel it; do not treat queued as passed.
              </p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {retryTarget && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void retry(retryTarget)}
                >
                  Retry
                </Button>
              )}
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void load()}>
                Refresh
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Ref</TableHead>
            <TableHead>Started</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((j) => (
            <TableRow
              key={j.id}
              className="cursor-pointer"
              data-mission-id={`job:${j.id}`}
              data-testid={`job-row-${j.id}`}
              onClick={() => {
                setCurrent(j);
                setOpen(true);
              }}
            >
              <TableCell className="font-mono text-xs">{j.id}</TableCell>
              <TableCell className="font-mono text-xs">{jobTypeLabel(j.kind, j.kind_detail)}</TableCell>
              <TableCell>
                <Badge className={tone[jobStatusLabel(j)] || tone.queued}>{jobStatusLabel(j)}</Badge>
              </TableCell>
              <TableCell className="font-mono text-xs">{shortOid(j.review_id || j.artifact_id, 12)}</TableCell>
              <TableCell className="text-xs" data-testid="job-started-at" title={j.created_at}>
                {formatJobStartedAt(j.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="mt-3 text-xs text-muted-foreground">
        {JOBS_FOOTER} {STALE_QUEUED_TECH}
      </p>
      </>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="gap-0 p-0 sm:max-w-md">
          {current && (
            <>
              <SheetHeader className="border-b border-border p-4" data-mission-id={`job:${current.id}`}>
                <SheetTitle className="font-mono text-sm">{current.id}</SheetTitle>
              </SheetHeader>
              <div className="space-y-3 p-4 text-sm">
                <div className="font-mono text-xs">
                  {jobTypeLabel(current.kind, current.kind_detail)} · {jobStatusLabel(current)}
                </div>
                {isSqlMindCheckKind(current.kind) && (
                  <p className="text-xs text-muted-foreground" data-testid="mind-check-not-a-gate">
                    {MIND_CHECK_JOB_NOTE}
                  </p>
                )}
                {isNonDeterministicJobKind(current.kind, current.kind_detail) && !isSqlMindCheckKind(current.kind) && (
                  <p className="text-xs text-muted-foreground">
                    stub job kind — not a deterministic JobsChecks run
                  </p>
                )}
                {userFacingJobError(current.error) ? (
                  <pre
                    className="overflow-auto rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs whitespace-pre-wrap"
                    data-testid="job-detail-error"
                  >
                    {userFacingJobError(current.error)}
                  </pre>
                ) : (
                  <p className="text-xs text-muted-foreground">No jobs.error.</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {!isNonDeterministicJobKind(current.kind, current.kind_detail) && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void retry(current)}
                    >
                      Retry
                    </Button>
                  )}
                  {(current.state === "queued" || current.state === "running") && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => void cancel(current)}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}
